import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { toast } from "sonner";
import { api, uploadAndCreatePost, errorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MerchantLayout } from "@/pages/merchant/MerchantLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card } from "@/components/ui/card";
import { Camera, Loader2, Image as ImageIcon, TicketPercent, Sparkles } from "lucide-react";
import TravelBookingSection, {
    TOURISM_DISTRICT_SLUGS,
    TRAVEL_CATEGORY_SLUGS,
} from "@/components/posts/TravelBookingSection";
import PartnerLinkSection from "@/components/posts/PartnerLinkSection";
import OfferGallery from "@/components/posts/OfferGallery";
import { PosterStudio } from "@/components/poster-studio/PosterStudio";
import { StickerEditor } from "@/components/poster-studio/StickerEditor";
import { Sticker as StickerIcon } from "lucide-react";

const MAX_MB = 8;

export default function MerchantOfferNew() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [posterFile, setPosterFile] = useState(null);
    const [posterPreview, setPosterPreview] = useState(null);
    // Phase 8 — poster source mode ("STUDIO" | "UPLOAD"). Studio produces a
    // remote poster_image_url; upload mode uses the original file flow.
    const [posterMode, setPosterMode] = useState("STUDIO");
    const [studioPosterUrl, setStudioPosterUrl] = useState(null);
    // Phase 10 — Sticker editor for uploaded images
    const [stickerOpen, setStickerOpen] = useState(false);
    const [profile, setProfile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // Travel & booking details (separate from react-hook-form to keep the
    // chip UX simple; merged into `details` at submit time).
    const [travel, setTravel] = useState({
        inclusions: [],
        booking_cta_type: null,
        booking_whatsapp: null,
        booking_phone: null,
        booking_price_label: null,
    });
    // Phase 6 — optional partner link
    const [partner, setPartner] = useState({ partner_url: "", partner_brand_slug: "" });
    // Multi-image gallery (ordered list of upload URLs)
    const [gallery, setGallery] = useState([]);

    const isTravel = TRAVEL_CATEGORY_SLUGS.includes(user?.business_category);
    const isTourismDistrict = TOURISM_DISTRICT_SLUGS.includes(user?.business_district);

    const {
        register,
        control,
        handleSubmit,
        watch,
        getValues,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            title: "",
            description: "",
            discount_percent: "",
            price_before: "",
            price_after: "",
            offer_code: "",
            terms: "",
            valid_to: "",
            coupon_enabled: false,
            coupon_discount_type: "PERCENT",
            coupon_discount_value: "",
            coupon_total_uses: "",
            coupon_terms: "",
            status: "PUBLISHED",
            delivery_zomato_url: "",
            delivery_swiggy_url: "",
        },
        shouldUnregister: false,
    });

    const values = watch();

    // Phase 8 — fetch shop profile summary for auto-branding on the poster.
    useEffect(() => {
        api.get("/me/profile-summary").then((r) => setProfile(r.data)).catch(() => setProfile(null));
    }, []);

    useEffect(() => {
        // If user later toggles coupon on, mirror the discount into coupon fields for convenience
        if (values.coupon_enabled && !values.coupon_discount_value && values.discount_percent) {
            setValue("coupon_discount_value", values.discount_percent);
        }
    }, [values.coupon_enabled, values.discount_percent, values.coupon_discount_value, setValue]);

    const onPoster = (file) => {
        if (!file) return;
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error(`Image must be under ${MAX_MB} MB`);
            return;
        }
        setPosterFile(file);
        const url = URL.createObjectURL(file);
        setPosterPreview((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return url;
        });
    };

    const toNum = (v) => {
        if (v === "" || v === null || v === undefined) return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    };

    const onSubmit = async (data) => {
        if (posterMode === "UPLOAD" && !posterFile) {
            toast.error("Please add a poster image");
            return;
        }
        if (posterMode === "STUDIO" && !studioPosterUrl) {
            toast.error("Click 'Generate poster' to lock in your studio design first");
            return;
        }
        // Belt-and-suspenders: prefer live values from getValues() over stale `data`.
        const latest = { ...data, ...getValues() };
        try {
            setSubmitting(true);

            const details = {
                business_type: user?.business_type,
            };
            const dp = toNum(latest.discount_percent);
            if (dp !== null) details.discount_percent = dp;
            const pb = toNum(latest.price_before);
            if (pb !== null) details.price_before = pb;
            const pa = toNum(latest.price_after);
            if (pa !== null) details.price_after = pa;
            if (latest.offer_code) details.offer_code = latest.offer_code;
            if (latest.terms) details.terms = latest.terms;
            // Phase 16 — Restaurant delivery links (only for restaurants category).
            if (user?.business_category === "restaurants") {
                if (latest.delivery_zomato_url?.trim()) details.delivery_zomato_url = latest.delivery_zomato_url.trim();
                if (latest.delivery_swiggy_url?.trim()) details.delivery_swiggy_url = latest.delivery_swiggy_url.trim();
            }

            if (latest.coupon_enabled) {
                const cv = toNum(latest.coupon_discount_value);
                const ct = toNum(latest.coupon_total_uses);
                if (cv === null || cv <= 0) {
                    toast.error("Enter a coupon discount value.");
                    setSubmitting(false);
                    return;
                }
                if (ct === null || ct < 1) {
                    toast.error("Enter total number of coupons (min 1).");
                    setSubmitting(false);
                    return;
                }
                details.coupon_enabled = true;
                details.coupon_discount_type = latest.coupon_discount_type || "PERCENT";
                details.coupon_discount_value = cv;
                details.coupon_total_uses = ct;
                if (latest.coupon_terms) details.coupon_terms = latest.coupon_terms;
            }

            // --- Phase 2c: Travel & Booking details for travel-category shops ---
            if (isTravel) {
                if (travel.inclusions && travel.inclusions.length) {
                    details.inclusions = travel.inclusions;
                }
                if (travel.booking_cta_type) {
                    const needWa = ["WHATSAPP", "BOTH"].includes(travel.booking_cta_type);
                    const needPh = ["CALL", "BOTH"].includes(travel.booking_cta_type);
                    const wa = (travel.booking_whatsapp || "").replace(/\D/g, "");
                    const ph = (travel.booking_phone || "").replace(/\D/g, "");
                    if (needWa && wa.length !== 10) {
                        toast.error("Enter a 10-digit WhatsApp number.");
                        setSubmitting(false);
                        return;
                    }
                    if (needPh && ph.length !== 10) {
                        toast.error("Enter a 10-digit phone number.");
                        setSubmitting(false);
                        return;
                    }
                    details.booking_cta_type = travel.booking_cta_type;
                    if (needWa) details.booking_whatsapp = wa;
                    if (needPh) details.booking_phone = ph;
                }
                if (travel.booking_price_label) {
                    details.booking_price_label = travel.booking_price_label.trim();
                }
            }

            // --- Phase 6: partner link (optional) ---
            const purl = (partner.partner_url || "").trim();
            const pslug = (partner.partner_brand_slug || "").trim();
            if (pslug && !purl) {
                toast.error("Partner URL is required when a brand is selected.");
                setSubmitting(false);
                return;
            }
            if (purl && !/^https?:\/\//i.test(purl)) {
                toast.error("Partner URL must start with http:// or https://");
                setSubmitting(false);
                return;
            }
            if (purl) details.partner_url = purl;
            if (pslug) details.partner_brand_slug = pslug;

            // Multi-image gallery (ordered list of URLs)
            if (gallery.length > 0) details.gallery = gallery;

            const payload = {
                type: "OFFER",
                title: (latest.title || "").trim(),
                description: latest.description || null,
                district_slug: user.business_district,
                category_slug: user.business_category,
                valid_to: latest.valid_to
                    ? new Date(latest.valid_to + "T23:59:59").toISOString()
                    : null,
                details,
                status: latest.status,
            };

            // eslint-disable-next-line no-console
            console.info("[MerchantOfferNew] Submitting payload:", payload);

            let post;
            if (posterMode === "STUDIO") {
                // Studio already uploaded the PNG and gave us a URL — post directly.
                const res = await api.post("/posts", { ...payload, poster_image_url: studioPosterUrl });
                post = res.data;
            } else {
                post = await uploadAndCreatePost(posterFile, payload);
            }
            toast.success(
                latest.status === "PUBLISHED"
                    ? "Offer published!"
                    : "Offer saved as draft.",
            );
            navigate(`/merchant/offers/${post.id}`, { replace: true });
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <MerchantLayout showTabs={false}>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="pb-32"
                data-testid="merchant-offer-new-form"
            >
                <div className="px-4 pt-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-orange-600">
                        New offer
                    </div>
                    <h1 className="text-2xl font-extrabold text-neutral-900">
                        Create an offer
                    </h1>
                    <p className="mt-1 text-xs text-neutral-500">
                        Live to customers in{" "}
                        <b>{user?.business_district || "your district"}</b> · Category{" "}
                        <b>{user?.business_category || "-"}</b>
                    </p>
                    {isTravel && isTourismDistrict && (
                        <div
                            className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-800"
                            data-testid="mon-tourism-hint"
                        >
                            <Sparkles size={11} /> Popular tourist district
                        </div>
                    )}
                </div>

                <div className="mt-4 space-y-4 px-4">
                    {/* Poster — Phase 8 tab switcher */}
                    <div className="rounded-2xl border border-neutral-200 bg-white p-4">
                        <div className="mb-3 flex items-center gap-2" data-testid="mon-poster-mode-tabs">
                            <button
                                type="button"
                                onClick={() => setPosterMode("STUDIO")}
                                data-testid="mon-poster-mode-studio"
                                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${posterMode === "STUDIO" ? "nest-gradient text-white" : "border border-neutral-300 bg-white text-neutral-700"}`}
                            >
                                ✨ Design a Poster
                            </button>
                            <button
                                type="button"
                                onClick={() => setPosterMode("UPLOAD")}
                                data-testid="mon-poster-mode-upload"
                                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${posterMode === "UPLOAD" ? "bg-neutral-900 text-white" : "border border-neutral-300 bg-white text-neutral-700"}`}
                            >
                                Upload my own
                            </button>
                        </div>
                        {posterMode === "STUDIO" ? (
                            <PosterStudio
                                initialDiscount={(() => {
                                    // Bug fix (Phase 8 P1): compute a live label from whatever the
                                    // merchant filled on Step 1. Percent wins; fall back to a FLAT
                                    // coupon value; else empty (studio shows placeholder).
                                    const pct = values.discount_percent;
                                    if (pct && !isNaN(Number(pct))) return `${Number(pct)}% OFF`;
                                    const cv = values.coupon_discount_value;
                                    if (values.coupon_enabled && cv && !isNaN(Number(cv))) {
                                        return values.coupon_discount_type === "FLAT" ? `₹${Number(cv)} OFF` : `${Number(cv)}% OFF`;
                                    }
                                    return "";
                                })()}
                                profile={profile}
                                apiClient={api}
                                onGenerated={setStudioPosterUrl}
                            />
                        ) : (
                    <div>
                        <Label className="text-xs">Poster image *</Label>
                        <label
                            className={`mt-1 flex aspect-[4/3] cursor-pointer items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed ${posterPreview ? "border-orange-400" : "border-neutral-300 bg-neutral-50"}`}
                            data-testid="mon-poster"
                        >
                            {posterPreview ? (
                                <img
                                    src={posterPreview}
                                    alt=""
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <div className="flex flex-col items-center gap-2 text-neutral-400">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full nest-gradient text-white">
                                        <Camera size={18} />
                                    </div>
                                    <div className="text-xs font-medium">Tap to add poster</div>
                                    <div className="text-[11px]">4:3 ratio · &lt; {MAX_MB} MB</div>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => onPoster(e.target.files?.[0])}
                            />
                        </label>
                        {posterPreview && (
                            <div className="mt-2 flex items-center justify-end gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setStickerOpen(true)}
                                    data-testid="upload-add-stickers-btn"
                                    className="text-xs"
                                >
                                    <StickerIcon size={14} className="mr-1" /> Add Stickers
                                </Button>
                            </div>
                        )}
                    </div>
                        )}
                    </div>

                    {/* Gallery images (up to 8, drag-to-reorder) */}
                    <OfferGallery
                        value={gallery}
                        onChange={setGallery}
                        testidPrefix="mon-gallery"
                    />

                    {/* Title + description */}
                    <div>
                        <Label className="text-xs">Title *</Label>
                        <Input
                            className="mt-1"
                            placeholder="e.g. 30% off on all groceries"
                            maxLength={120}
                            data-testid="mon-title"
                            {...register("title", { required: true, minLength: 3 })}
                        />
                        {errors.title && (
                            <p className="mt-1 text-xs text-rose-600">Title is required</p>
                        )}
                    </div>
                    <div>
                        <Label className="text-xs">Short description</Label>
                        <Textarea
                            className="mt-1"
                            rows={3}
                            placeholder="Quick pitch — what makes this deal awesome?"
                            maxLength={500}
                            data-testid="mon-description"
                            {...register("description")}
                        />
                    </div>

                    {/* Discount */}
                    <Card className="rounded-2xl border border-neutral-200 p-4">
                        <div className="mb-3 flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
                                <TicketPercent size={16} />
                            </div>
                            <div className="text-sm font-bold">Discount details</div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                                <Label className="text-xs">Valid until</Label>
                                <Input
                                    className="mt-1"
                                    type="date"
                                    data-testid="mon-valid-to"
                                    {...register("valid_to")}
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Discount %</Label>
                                <Input
                                    className="mt-1"
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    max={100}
                                    placeholder="e.g. 30"
                                    data-testid="mon-discount-percent"
                                    {...register("discount_percent")}
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Offer code</Label>
                                <Input
                                    className="mt-1"
                                    placeholder="OPTIONAL"
                                    data-testid="mon-offer-code"
                                    {...register("offer_code")}
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Price before</Label>
                                <Input
                                    className="mt-1"
                                    type="number"
                                    inputMode="decimal"
                                    min={0}
                                    placeholder="₹"
                                    data-testid="mon-price-before"
                                    {...register("price_before")}
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Price after</Label>
                                <Input
                                    className="mt-1"
                                    type="number"
                                    inputMode="decimal"
                                    min={0}
                                    placeholder="₹"
                                    data-testid="mon-price-after"
                                    {...register("price_after")}
                                />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-xs">Terms</Label>
                                <Textarea
                                    className="mt-1"
                                    rows={2}
                                    placeholder="e.g. Not valid on MRP items. Valid till stocks last."
                                    maxLength={500}
                                    data-testid="mon-terms"
                                    {...register("terms")}
                                />
                            </div>
                        </div>
                    </Card>

                    {/* Phase 16 — Restaurant delivery links */}
                    {user?.business_category === "restaurants" && (
                        <Card className="rounded-2xl border border-neutral-200 p-4" data-testid="merchant-restaurant-delivery-section">
                            <div className="mb-2">
                                <div className="text-sm font-bold text-neutral-800">Delivery links (optional)</div>
                                <div className="text-xs text-neutral-500">
                                    If you're on Zomato or Swiggy, paste the direct link — customers can order in one tap.
                                </div>
                            </div>
                            <div className="mt-3 space-y-3">
                                <div>
                                    <Label className="text-xs">Zomato URL</Label>
                                    <Input
                                        className="mt-1"
                                        placeholder="https://www.zomato.com/…"
                                        data-testid="merchant-restaurant-zomato-url"
                                        {...register("delivery_zomato_url")}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Swiggy URL</Label>
                                    <Input
                                        className="mt-1"
                                        placeholder="https://www.swiggy.com/restaurants/…"
                                        data-testid="merchant-restaurant-swiggy-url"
                                        {...register("delivery_swiggy_url")}
                                    />
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* Digital Coupon */}
                    <Card className="rounded-2xl border border-neutral-200 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-sm font-bold text-neutral-800">
                                    Digital coupons
                                </div>
                                <div className="text-xs text-neutral-500">
                                    Customers can claim a unique code — redeem in store.
                                </div>
                            </div>
                            <Controller
                                control={control}
                                name="coupon_enabled"
                                render={({ field }) => (
                                    <Switch
                                        checked={!!field.value}
                                        onCheckedChange={field.onChange}
                                        data-testid="mon-coupon-toggle"
                                    />
                                )}
                            />
                        </div>

                        {values.coupon_enabled && (
                            <div className="mt-4 grid grid-cols-2 gap-3">
                                <div className="col-span-2">
                                    <Label className="text-xs">Discount type</Label>
                                    <Controller
                                        control={control}
                                        name="coupon_discount_type"
                                        render={({ field }) => (
                                            <RadioGroup
                                                value={field.value}
                                                onValueChange={field.onChange}
                                                className="mt-1 grid grid-cols-2 gap-2"
                                            >
                                                {["PERCENT", "FLAT"].map((t) => (
                                                    <label
                                                        key={t}
                                                        className={`flex cursor-pointer items-center gap-2 rounded-xl border p-2 ${field.value === t ? "border-orange-500 bg-orange-50" : "border-neutral-200"}`}
                                                        data-testid={`mon-coupon-type-${t}`}
                                                    >
                                                        <RadioGroupItem value={t} id={`ct-${t}`} />
                                                        <span className="text-xs font-semibold">
                                                            {t === "PERCENT" ? "% off" : "Flat ₹"}
                                                        </span>
                                                    </label>
                                                ))}
                                            </RadioGroup>
                                        )}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Value *</Label>
                                    <Input
                                        className="mt-1"
                                        type="number"
                                        min={1}
                                        placeholder={values.coupon_discount_type === "PERCENT" ? "e.g. 10" : "e.g. 100"}
                                        data-testid="mon-coupon-value"
                                        {...register("coupon_discount_value", {
                                            required: values.coupon_enabled,
                                        })}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Total coupons *</Label>
                                    <Input
                                        className="mt-1"
                                        type="number"
                                        min={1}
                                        placeholder="e.g. 100"
                                        data-testid="mon-coupon-total"
                                        {...register("coupon_total_uses", {
                                            required: values.coupon_enabled,
                                        })}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-xs">Coupon terms</Label>
                                    <Textarea
                                        className="mt-1"
                                        rows={2}
                                        placeholder="e.g. Show code at billing counter."
                                        maxLength={500}
                                        data-testid="mon-coupon-terms"
                                        {...register("coupon_terms")}
                                    />
                                </div>
                            </div>
                        )}
                    </Card>

                    {isTravel && (
                        <TravelBookingSection
                            value={travel}
                            onChange={setTravel}
                            testidPrefix="mon-tb"
                        />
                    )}

                    <PartnerLinkSection
                        value={partner}
                        onChange={setPartner}
                        postType="OFFER"
                        testidPrefix="mon-partner"
                    />

                    {/* Status */}
                    <div>
                        <Label className="text-xs">Save as</Label>
                        <Controller
                            control={control}
                            name="status"
                            render={({ field }) => (
                                <RadioGroup
                                    value={field.value}
                                    onValueChange={field.onChange}
                                    className="mt-2 grid grid-cols-2 gap-3"
                                >
                                    {[
                                        { v: "PUBLISHED", label: "Publish now", desc: "Live to customers" },
                                        { v: "DRAFT", label: "Save as draft", desc: "Publish later" },
                                    ].map((o) => (
                                        <label
                                            key={o.v}
                                            className={`flex cursor-pointer flex-col gap-1 rounded-xl border p-3 ${field.value === o.v ? "border-orange-500 bg-orange-50" : "border-neutral-200"}`}
                                            data-testid={`mon-status-${o.v}`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <RadioGroupItem value={o.v} id={`st-${o.v}`} />
                                                <span className="text-sm font-semibold">{o.label}</span>
                                            </div>
                                            <span className="text-[11px] text-neutral-500 pl-6">{o.desc}</span>
                                        </label>
                                    ))}
                                </RadioGroup>
                            )}
                        />
                    </div>
                </div>

                <div className="fixed inset-x-0 bottom-0 z-30 mx-auto max-w-[560px] border-t border-neutral-200 bg-white p-4">
                    <Button
                        type="submit"
                        disabled={submitting}
                        className="h-12 w-full nest-gradient text-base font-semibold text-white"
                        data-testid="mon-submit"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="mr-2 animate-spin" size={18} />
                                Publishing…
                            </>
                        ) : values.status === "DRAFT" ? (
                            "Save draft"
                        ) : (
                            "Publish offer"
                        )}
                    </Button>
                </div>
            </form>
            <StickerEditor
                open={stickerOpen}
                onOpenChange={setStickerOpen}
                imageUrl={posterPreview}
                onSave={(file, blobUrl) => {
                    setPosterFile(file);
                    setPosterPreview((prev) => {
                        if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
                        return blobUrl;
                    });
                    toast.success("Stickers added ✓");
                }}
            />
        </MerchantLayout>
    );
}
